import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelProps,
  type ElementSizingMode,
  type ElementTitleStyleProps,
  getActiveBinding,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { generatePdfViewerFormField } from './generation/form-field';
import { pdfViewerV2Migration } from './migrations/002-pdf-viewer.migration';
import { pdfViewerV3PaddingToMarginMigration } from './migrations/003-pdf-viewer-padding-to-margin.migration';
import { PdfViewerComponent } from './pdf-viewer.component';
import { PdfViewerPropsBuilder } from './pdf-viewer.props-builder';

export { transformToPdfViewer } from './pdf-viewer.transforms';
export type PdfViewerDef = WithProps<PdfViewerPropsModel> & WithIoConfig;
export type PdfViewerElement = CanvasElementFrom<'pdfViewer'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    pdfViewer: PdfViewerDef;
  }
}

const DEFAULT_WIDTH = 160;
const DEFAULT_HEIGHT = 300;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 999;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];

registerElementDefinition({
  name: 'PDF Viewer',
  type: 'pdfViewer',
  componentType: PdfViewerComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  propsBuilder: PdfViewerPropsBuilder,
  generation: {
    formField: generatePdfViewerFormField,
  },
  tags: ['pdf', 'url', 'file'],
  groups: ['advanced'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  version: 3,
  migrations: {
    2: pdfViewerV2Migration,
    3: pdfViewerV3PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [],
  };
}

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type PdfViewerPropsModel = PropsModel & {
  content: {
    pdf: {
      downloadEnabled: boolean;
      printEnabled: boolean;
      url: string; // typed or uploaded URL
    };
    title: ElementLabelProps;
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    titleBlock: ElementTitleStyleProps;
    label: {
      color: PropertiesConfigInputColorModel;
    };
    border: {
      outline: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
      width: ValueWithUnitOfMeasure;
    };
  };
};

function getDefaults(): PropsOf<'pdfViewer'> {
  return {
    content: {
      pdf: {
        downloadEnabled: true,
        printEnabled: true,
        url: '',
      },
      title: {
        enabled: false,
        align: 'left',
        label: 'PDF',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      label: {
        color: { color1: '$t.title', selectorType: 'solid' },
      },
      border: {
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        width: '$t.containerOutlineWidth',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('pdfViewer', [
    epc.section('content.pdf', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [
          epc.fields.uploadField('content.pdf.url', 'Type URL to PDF or upload the file', {
            placeholderExtension: 'pdf',
            dataType: 'pdf',
          }),
        ],
        [
          ...epc.utils.disabledFields(
            [
              epc.fields.selectDataSourceField({
                key: 'value',
                label: 'Data Source',
                sources: 'list',
                bindings: [],
              }),
              epc.fields.selectFieldFromModel(
                'data.?key:value.bindings.value',
                'PDF',
                'data.?key:value.dataSource.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  placeholder: 'Select column',
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                },
              ),
            ],
            (props: PropsOf<'pdfViewer'>) =>
              getActiveBinding(props.data)?.dataSource?.sourceType === 'formContainer'
                ? 'Source cannot be changed when used inside Form'
                : '',
          ),
        ],
      ),
      epc.subsections.subsection('content.pdf', 'Options', [
        epc.fields.toggleField('content.pdf.downloadEnabled', 'Enable PDF Download'),
        epc.fields.toggleField('content.pdf.printEnabled', 'Enable PDF Print'),
      ]),
      epc.subsections.subsection('content.pdf', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: DEFAULT_HEIGHT, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.label', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.border', 'Border', [
          epc.fields.colorField('style.border.outline', 'Outline', {
            allowGradient: false,
            hasOutlineSides: true,
          }),
          epc.fields.radiusField('style.border.radius'),
          epc.fields.sliderField('style.border.width', 'Width', [{ min: 0, max: 25, unit: 'px' }]),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.label.color',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
